import {useCallback, useEffect, useState, type ReactElement} from 'react'
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import {makeStyles} from '@material-ui/core'
import {AlertDialog} from '../../Common/Dialog'
import {backendGetRequest} from '../../../common/utils/requests'
import {renderMaintenanceInline, renderMaintenanceMarkdown} from './renderMaintenanceMarkdown'

const recheckIntervalMs = 30000
const dismissStorageKey = 'skyground.dismissedMaintenanceWindows'

type ActionButton = 'ok' | 'close'

type MaintenanceNotice = {
  id: number
  title: string
  description: string
  actionButtons: ActionButton[]
  startAt: string
  endAt: string
}

const brandRed = '#A50000'

const useStyles = makeStyles({
  alertBody: {
    '&&': {
      padding: 0,
      background: '#FFFFFF',
      overflow: 'hidden',
      minWidth: 340,
    },
  },
  title: {
    background: brandRed,
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: 700,
    lineHeight: '26px',
    padding: '18px 22px',
    '& a': {
      color: '#FFFFFF',
      textDecoration: 'underline',
    },
  },
  description: {
    color: '#6E1212',
    fontSize: 15,
    lineHeight: '24px',
    padding: '16px 22px 4px',
    '& p': {
      margin: '0 0 10px',
    },
    '& ul, & ol': {
      margin: '0 0 10px',
      paddingLeft: 20,
    },
    '& a': {
      color: brandRed,
      fontWeight: 700,
      textDecoration: 'underline',
    },
    '& u': {
      textDecoration: 'underline',
    },
  },
  buttonRow: {
    display: 'flex',
    padding: '8px 22px 20px',
  },
  okButtonClassName: {
    '&&': {
      flex: 1,
      textTransform: 'uppercase',
      minHeight: 42,
      backgroundColor: brandRed,
      color: '#FFFFFF',
      boxShadow: 'none',
    },
    '&&:hover': {
      backgroundColor: '#860000',
      boxShadow: 'none',
    },
  },
  closeButtonClassName: {
    '&&': {
      flex: 1,
      textTransform: 'uppercase',
      minHeight: 42,
      color: brandRed,
      borderColor: brandRed,
      backgroundColor: '#FFFFFF',
    },
    '&&:hover': {
      borderColor: '#860000',
      backgroundColor: '#FFF1F1',
    },
  },
})

const readDismissed = (): number[] => {
  try {
    const raw = window.sessionStorage.getItem(dismissStorageKey)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'number') : []
  } catch {
    return []
  }
}

const rememberDismissed = (ids: number[]) => {
  window.sessionStorage.setItem(dismissStorageKey, JSON.stringify(ids))
}

export function ScheduledMaintenancePopup(): ReactElement | null {
  const classes = useStyles()
  const [notices, setNotices] = useState<MaintenanceNotice[]>([])
  const [dismissedIds, setDismissedIds] = useState<number[]>(() => readDismissed())

  const loadNotices = useCallback(async () => {
    try {
      const response = await backendGetRequest('/api/v2/dashboard/maintenance-windows/active', true)
      if (!response.ok) {
        setNotices([])
        return
      }
      const body = await response.json()
      setNotices(Array.isArray(body.windows) ? body.windows : [])
    } catch {
      setNotices([])
    }
  }, [])

  useEffect(() => {
    loadNotices()
    const intervalId = window.setInterval(loadNotices, recheckIntervalMs)
    return () => window.clearInterval(intervalId)
  }, [loadNotices])

  const current = notices.find((notice) => !dismissedIds.includes(notice.id)) ?? null

  const dismiss = () => {
    if (!current) return
    const next = dismissedIds.includes(current.id) ? dismissedIds : [...dismissedIds, current.id]
    setDismissedIds(next)
    rememberDismissed(next)
  }

  if (!current) return null

  const buttons = current.actionButtons?.length ? current.actionButtons : ['ok']

  return (
    <AlertDialog
      title=""
      maxWidth="sm"
      open
      alertBodyClass={classes.alertBody}
      onClose={buttons.includes('close') ? dismiss : undefined}
    >
      <Grid container direction="column" justifyContent="space-between">
        <Grid item className={classes.title} dangerouslySetInnerHTML={{__html: renderMaintenanceInline(current.title)}} />
        <Grid item
          className={classes.description}
          dangerouslySetInnerHTML={{__html: renderMaintenanceMarkdown(current.description)}}
        />
        <Grid item className={classes.buttonRow}>
          {buttons.includes('ok') && (
            <Button
              className={classes.okButtonClassName}
              style={buttons.includes('close') ? {marginRight: 8} : undefined}
              variant="contained"
              onClick={dismiss}
            >
              OK
            </Button>
          )}
          {buttons.includes('close') && (
            <Button className={classes.closeButtonClassName} variant="outlined" onClick={dismiss}>
              Close
            </Button>
          )}
        </Grid>
      </Grid>
    </AlertDialog>
  )
}
